import { gql } from '@apollo/client';
import { toNumber } from 'lodash-es';
import { getApolloClient } from 'api/apolloClient';
import {
  type Listing,
  type PsaGroupedByCountryCodeResult,
  type QueryCommercialPropertiesArgs,
  type Query,
  type QueryResidentialPropertiesArgs,
  type QueryVacationPropertiesArgs,
  type SearchParamsInput,
} from 'server/types/graphql';

// ------------------------------------
// Constants
// ------------------------------------
export const EMPTY_RESULT_LIST = {
  commercialHits: [] as Listing[],
  residentialHits: [] as Listing[],
  vacationHitGroups: [] as PsaGroupedByCountryCodeResult[],
  isLoading: false,
};

// ------------------------------------
// Action constants
// ------------------------------------
export const actionTypes = {
  RECEIVE_RESIDENTIAL_PROPERTIES: 'RECEIVE_RESIDENTIAL_PROPERTIES',
  RECEIVE_COMMERCIAL_PROPERTIES: 'RECEIVE_COMMERCIAL_PROPERTIES',
  RECEIVE_VACATION_PROPERTIES: 'RECEIVE_VACATION_PROPERTIES',
  START_LOADING_PROPERTIES: 'START_LOADING_PROPERTIES',
  DONE_LOADING_PROPERTIES: 'DONE_LOADING_PROPERTIES',
};

// ------------------------------------
// Actions
// ------------------------------------
export const setStartLoadingProperties = () => ({
  type: actionTypes.START_LOADING_PROPERTIES,
});

export const setDoneLoadingProperties = () => ({
  type: actionTypes.DONE_LOADING_PROPERTIES,
});

export const receiveCommericalProperties = (hits: Listing[]) => ({
  type: actionTypes.RECEIVE_COMMERCIAL_PROPERTIES,
  hits,
});

export const receiveResidentialProperties = (hits: Listing[]) => ({
  type: actionTypes.RECEIVE_RESIDENTIAL_PROPERTIES,
  hits,
});

export const receiveVacationProperties = (
  groups: PsaGroupedByCountryCodeResult[],
) => ({
  type: actionTypes.RECEIVE_VACATION_PROPERTIES,
  groups,
});

const AUGMENTATION_FIELDS = gql`
  fragment AugmentationFields on Augmentation {
    value
    citation
    citationSource
  }
`;

const HITS_FRAGMENT = gql`
  ${AUGMENTATION_FIELDS}
  fragment HitsFragment on Listing {
    meta {
      hasChildren
    }
    addressString
    typedBadges {
      ... on BadgeRegular {
        augmentation {
          ...AugmentationFields
        }
        type
        shownOn
        details {
          value
        }
      }
    }
    exposeId
    isPrivate
    headline
    links {
      targetURL
    }
    mainKeyFacts {
      value
      label
      augmentation {
        ...AugmentationFields
      }
    }
    primaryPriceKeyFact {
      value
      label
    }
    realtorLogoImageProps {
      src
    }
    primaryPictureImageProps {
      src
    }
  }
`;

const GET_COMMERCIAL_PROPERTIES = gql`
  ${HITS_FRAGMENT}

  query commercialProperties($params: SearchParamsInput) {
    commercialProperties(params: $params) {
      ...HitsFragment
    }
  }
`;

const GET_RESIDENTIAL_PROPERTIES = gql`
  ${HITS_FRAGMENT}

  query residentialProperties($params: SearchParamsInput) {
    residentialProperties(params: $params) {
      ...HitsFragment
    }
  }
`;

const GET_VACATION_PROPERTIES = gql`
  ${HITS_FRAGMENT}

  query vacationProperties($accountCwid: String, $size: Int) {
    vacationProperties(accountCwid: $accountCwid, size: $size) {
      countryCode
      data {
        hits {
          ...HitsFragment
        }
      }
    }
  }
`;

/**
 * Fetches the complete list of properties for the existing page.
 */
export const fetchProperties =
  (query: SearchParamsInput) => async (dispatch: any) => {
    dispatch(setStartLoadingProperties());

    try {
      // Fetch all three property types in parallel
      const [commercialResult, residentialResult, vacationResult] =
        await Promise.allSettled([
          getApolloClient().query<
            Pick<Query, 'commercialProperties'>,
            QueryCommercialPropertiesArgs
          >({
            query: GET_COMMERCIAL_PROPERTIES,
            variables: { params: query },
          }),
          getApolloClient().query<
            Pick<Query, 'residentialProperties'>,
            QueryResidentialPropertiesArgs
          >({
            query: GET_RESIDENTIAL_PROPERTIES,
            variables: { params: query },
          }),
          getApolloClient().query<
            Pick<Query, 'vacationProperties'>,
            QueryVacationPropertiesArgs
          >({
            query: GET_VACATION_PROPERTIES,
            variables: {
              accountCwid: query.accountCwid,
              size: toNumber(query.size),
            },
          }),
        ]);

      // Dispatch commercial properties
      if (
        commercialResult.status === 'fulfilled' &&
        commercialResult.value.data.commercialProperties
      ) {
        dispatch(
          receiveCommericalProperties(
            commercialResult.value.data.commercialProperties,
          ),
        );
      } else {
        dispatch(receiveCommericalProperties(EMPTY_RESULT_LIST.commercialHits));
      }

      // Dispatch residential properties
      if (
        residentialResult.status === 'fulfilled' &&
        residentialResult.value.data.residentialProperties
      ) {
        dispatch(
          receiveResidentialProperties(
            residentialResult.value.data.residentialProperties,
          ),
        );
      } else {
        dispatch(
          receiveResidentialProperties(EMPTY_RESULT_LIST.residentialHits),
        );
      }

      // Dispatch vacation properties
      if (
        vacationResult.status === 'fulfilled' &&
        vacationResult.value.data.vacationProperties
      ) {
        dispatch(
          receiveVacationProperties(
            vacationResult.value.data.vacationProperties,
          ),
        );
      } else {
        dispatch(
          receiveVacationProperties(EMPTY_RESULT_LIST.vacationHitGroups),
        );
      }
    } finally {
      dispatch(setDoneLoadingProperties());
    }
  };

// ------------------------------------
// Reducer
// ------------------------------------
export const reducer = (state = EMPTY_RESULT_LIST, action: any) => {
  switch (action.type) {
    case actionTypes.RECEIVE_COMMERCIAL_PROPERTIES:
      return {
        ...state,
        commercialHits: action.hits,
      };
    case actionTypes.RECEIVE_RESIDENTIAL_PROPERTIES:
      return {
        ...state,
        residentialHits: action.hits,
      };
    case actionTypes.RECEIVE_VACATION_PROPERTIES:
      return {
        ...state,
        vacationHitGroups: action.groups,
      };
    case actionTypes.START_LOADING_PROPERTIES:
      return {
        ...state,
        isLoading: true,
      };
    case actionTypes.DONE_LOADING_PROPERTIES:
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
};
